import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { useEffect, type ReactNode } from "react";

import appCss from "../styles.css?url";
import { reportLovableError } from "../lib/lovable-error-reporting";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { WhatsAppFloat } from "../components/WhatsAppFloat";
import { SITE } from "../lib/site";
import iconAsset from "../assets/brand-mark.png.asset.json";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Página não encontrada</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          A página que você procura não existe ou foi movida.
        </p>
        <div className="mt-6">
          <Link to="/" className="btn-accent">Voltar para o início</Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          Esta página não carregou
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Algo deu errado. Você pode tentar novamente ou voltar para a capa.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { router.invalidate(); reset(); }}
            className="btn-primary"
          >
            Tentar novamente
          </button>
          <a href="/" className="btn-ghost">Ir para a capa</a>
        </div>
      </div>
    </div>
  );
}

const ROOT_TITLE = "Dedetizadora em Chapecó — Controle de Pragas SC";
const ROOT_DESC = "Dedetizadora em Chapecó/SC: baratas, ratos, cupins, formigas e insetos. Atendimento residencial, comercial e industrial com laudo e garantia.";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: ROOT_TITLE },
      { name: "description", content: ROOT_DESC },
      { name: "author", content: SITE.name },
      { name: "theme-color", content: "#FBBF24" },
      { property: "og:site_name", content: SITE.name },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "pt_BR" },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:title", content: ROOT_TITLE },
      { name: "twitter:title", content: ROOT_TITLE },
      { property: "og:description", content: ROOT_DESC },
      { name: "twitter:description", content: ROOT_DESC },
      { title: "Dedetizadora em Chapecó" },
      { property: "og:title", content: "Dedetizadora em Chapecó" },
      { name: "twitter:title", content: "Dedetizadora em Chapecó" },
      { name: "description", content: "Atendimento em toda a região, Chapecó e cidades em torno, atendemos todos os bairros de Chapecó." },
      { property: "og:description", content: "Atendimento em toda a região, Chapecó e cidades em torno, atendemos todos os bairros de Chapecó." },
      { name: "twitter:description", content: "Atendimento em toda a região, Chapecó e cidades em torno, atendemos todos os bairros de Chapecó." },
      { property: "og:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/e05172a0-ed72-4ef4-98fa-79b44e1ff793/id-preview-4c04b174--1653fc37-6bcc-4d12-8a4e-a2290afbbb72.lovable.app-1783826898892.png" },
      { name: "twitter:image", content: "https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/e05172a0-ed72-4ef4-98fa-79b44e1ff793/id-preview-4c04b174--1653fc37-6bcc-4d12-8a4e-a2290afbbb72.lovable.app-1783826898892.png" },
    ],
    links: [
      { rel: "icon", href: iconAsset.url, type: "image/png" },
      { rel: "apple-touch-icon", href: iconAsset.url },
      { rel: "stylesheet", href: appCss },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "NewsMediaOrganization",
              "@id": "/#organization",
              name: SITE.name,
              logo: iconAsset.url,
              description: SITE.description,
              email: SITE.email,
            },
            {
              "@type": "WebSite",
              "@id": "/#website",
              name: SITE.name,
              description: SITE.description,
              inLanguage: "pt-BR",
              publisher: { "@id": "/#organization" },
            },
          ],
        }),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: ReactNode }) {
  return (
    <html lang="pt-BR">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <QueryClientProvider client={queryClient}>
      <div className="flex min-h-screen flex-col">
        <Header />
        <main className="flex-1"><Outlet /></main>
        <Footer />
        <WhatsAppFloat />
      </div>
    </QueryClientProvider>
  );
}
